import {
	Box,
	createStyles,
	getDefaultZIndex,
	Group,
	Stack,
} from '@mantine/core';
import { useWindowResize } from '@repo/common/hooks/useWindowResize';
import { observer } from 'mobx-react-lite';
import { useState, type ReactNode } from 'react';
import { useSidebarBreakpoints } from '../../hooks/useRightSidebarPortalBehavior';
import {
	RIGHT_SIDEBAR_TRANSITION_DURATION,
	RIGHT_SIDEBAR_TRANSITION_TIMING_FUNCTION,
	TOP_NAVBAR_HEIGHT,
} from '../SecodaAppShell/constants';
import { ResizeRightSidebar } from './ResizeRightSidebar';
import type { RightSidebarStore } from './store';
import { rightSidebarStore } from './store';

const useStyles = createStyles(
	(
		theme,
		{
			store,
			isDragging,
			mobileBreakpointQuery,
		}: {
			store: RightSidebarStore;
			isDragging: boolean;
			mobileBreakpointQuery: string;
		}
	) => {
		const collapsed = store.collapsed || false;
		return {
			asideWrapper: {
				width: `calc(${store.width}px - 1px)`,
				minWidth: `calc(${store.width}px - 1px)`,
				height: `calc(100vh - ${TOP_NAVBAR_HEIGHT}px)`,
				scrollbarColor: `${theme.other.getColor(
					'fill/tertiary/default'
				)} transparent`,
				scrollbarWidth: 'thin',
				overflowY: 'auto',
				scrollbarGutter: 'stable',
				transitionProperty: 'width',
				transitionTimingFunction: RIGHT_SIDEBAR_TRANSITION_TIMING_FUNCTION,
				transitionDuration: RIGHT_SIDEBAR_TRANSITION_DURATION,
			},
			wrapper: {
				height: '100%',
				width: collapsed ? 0 : store.width,
				opacity: collapsed ? 0 : 1,
				position: 'relative',
				...(isDragging
					? {}
					: {
							transitionProperty: 'width',
							transitionTimingFunction:
								RIGHT_SIDEBAR_TRANSITION_TIMING_FUNCTION,
							transitionDuration: RIGHT_SIDEBAR_TRANSITION_DURATION,
						}),
				backgroundColor: theme.other.getColor('surface/app/default'),

				[theme.fn.smallerThan('md')]: {
					position: 'fixed',
					top: 0,
					right: 0,
					zIndex: getDefaultZIndex('overlay') + 1,
					boxShadow: theme.shadows.lg,
				},

				'@media print': {
					display: 'none',
				},
			},
			divider: {
				position: 'absolute',
				height: '100vh',
				transitionDelay: '200ms',

				[theme.fn.smallerThan('md')]: {
					display: 'none',
				},
			},
			overlay: {
				display: 'none',
				position: 'fixed',
				top: 0,
				left: 0,
				width: '100vw',
				height: '100dvh',
				backgroundColor: 'rgba(0, 0, 0, 0.24)',
				zIndex: getDefaultZIndex('overlay'),

				[`@media ${mobileBreakpointQuery}`]: {
					display: 'block',
				},
			},
		};
	}
);

export const RightSidebarWrapperController = observer(
	({
		classNames,
		children,
		store,
	}: {
		classNames?: Record<string, string>;
		children?: ReactNode;
		store: RightSidebarStore;
	}) => {
		const [isDragging, setIsDragging] = useState(false);
		const { autoCollapseBreakpoint, mobileBreakpointQuery } =
			useSidebarBreakpoints();
		const { classes, cx } = useStyles({
			store,
			isDragging,
			mobileBreakpointQuery,
		});

		useWindowResize(({ current, previous }) => {
			// Make sure that auto collapse happens before overlay is shown
			if (
				current.width < autoCollapseBreakpoint &&
				previous.width >= autoCollapseBreakpoint
			) {
				store.setCollapsedByWindowResize(true);
			} else if (
				current.width >= autoCollapseBreakpoint &&
				previous.width < autoCollapseBreakpoint &&
				store.autoCollapsedByWindowResize
			) {
				store.setCollapsedByWindowResize(false);
			}
		}, []);

		// Don't render if collapsed and no children
		if (store.collapsed && !children) {
			return null;
		}

		return (
			<>
				<Group
					className={cx(classes.wrapper, classNames?.wrapper)}
					spacing={0}
					noWrap
					data-testid="page-sidebar-wrapper"
				>
					{/* <Divider className={classes.divider} orientation="vertical" /> */}
					<Stack spacing={0}>
						<Box h={TOP_NAVBAR_HEIGHT} />
						<Stack
							className={cx(classes.asideWrapper, classNames?.asideWrapper)}
							spacing={0}
						>
							{children}
						</Stack>
					</Stack>
					<ResizeRightSidebar
						store={store}
						onStartDragging={() => setIsDragging(true)}
						onStopDragging={() => setIsDragging(false)}
					/>
				</Group>
				{!store.collapsed && (
					<Box
						className={classes.overlay}
						onClick={() => store.setCollapsed(true)}
					/>
				)}
			</>
		);
	}
);

export interface RightSidebarWrapperProps {
	classNames?: Record<string, string>;
	children?: ReactNode;
}

export const RightSidebarWrapper = observer(
	({ classNames, children }: RightSidebarWrapperProps) => (
		<RightSidebarWrapperController
			classNames={classNames}
			children={children}
			store={rightSidebarStore}
		/>
	)
);
